import { Check, GripVertical, RotateCcw, Shield, UserRound } from "lucide-react";
import { useState, type PointerEvent as ReactPointerEvent } from "react";
import { Button } from "@/components/ui/button";

type Officer = { id: string; name: string; role: string };
type Slot = { id: string; time: string; site: string; officerId: string | null };

const OFFICERS: Officer[] = [
  { id: "torres", name: "S. Torres", role: "Patrol" },
  { id: "chen", name: "A. Chen", role: "Supervisor" },
  { id: "walker", name: "M. Walker", role: "Officer" },
];
const INITIAL_SLOTS: Slot[] = [
  { id: "north", time: "18:00–02:00", site: "North Campus", officerId: "torres" },
  { id: "dock", time: "20:00–04:00", site: "Loading Dock", officerId: null },
  { id: "lobby", time: "22:00–06:00", site: "Main Lobby", officerId: "chen" },
];

export function SchedulingDemo({ detailed = false }: { detailed?: boolean }) {
  const [slots, setSlots] = useState(INITIAL_SLOTS);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [message, setMessage] = useState("DRAG AN OFFICER TO A SHIFT");

  const assign = (slotId: string, officerId: string) => {
    const officer = OFFICERS.find((item) => item.id === officerId);
    const slot = slots.find((item) => item.id === slotId);
    if (!officer || !slot) return;
    setSlots((current) => current.map((item) => item.id === slotId ? { ...item, officerId } : item));
    setMessage(`${officer.name.toUpperCase()} DEPLOYED TO ${slot.site.toUpperCase()}`);
  };

  const beginPointerDrag = (event: ReactPointerEvent, officerId: string) => {
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDraggedId(officerId);
    setMessage("DROP ON AN OPEN OR ASSIGNED SHIFT");
  };

  const endPointerDrag = (event: ReactPointerEvent) => {
    if (!draggedId) return;
    const target = document.elementFromPoint(event.clientX, event.clientY)?.closest<HTMLElement>("[data-shift-slot]");
    const slotId = target?.dataset.shiftSlot;
    if (slotId) assign(slotId, draggedId);
    else setMessage("NO CHANGE — TRY A SHIFT SLOT");
    setDraggedId(null);
  };

  return (
    <div className={`mr-schedule-demo ${detailed ? "is-detailed" : ""}`} aria-label="Interactive drag-and-drop shift scheduling demonstration">
      <div className="mr-demo-toolbar"><span><i /> SCHEDULE BUILDER</span><span><Check /> COVERAGE LIVE</span></div>
      <div className="mr-schedule-layout">
        <div className="mr-officer-bank">
          <p>AVAILABLE OFFICERS</p>
          {OFFICERS.map((officer) => (
            <button key={officer.id} type="button" className={draggedId === officer.id ? "is-dragging" : ""} draggable
              onDragStart={(event) => { event.dataTransfer.setData("text/plain", officer.id); setDraggedId(officer.id); }}
              onDragEnd={() => setDraggedId(null)} onPointerDown={(event) => beginPointerDrag(event, officer.id)} onPointerUp={endPointerDrag}>
              <GripVertical /><span><strong>{officer.name}</strong><small>{officer.role}</small></span>
            </button>
          ))}
        </div>
        <div className="mr-shift-board">
          <div className="mr-shift-board-head"><span>TODAY // NIGHT WATCH</span><b>{slots.filter((slot) => slot.officerId).length}/{slots.length} COVERED</b></div>
          {slots.map((slot) => {
            const officer = OFFICERS.find((item) => item.id === slot.officerId);
            return (
              <div key={slot.id} className={`mr-shift-slot ${draggedId ? "is-ready" : ""}`} data-shift-slot={slot.id}
                onDragOver={(event) => event.preventDefault()} onDrop={(event) => { event.preventDefault(); assign(slot.id, event.dataTransfer.getData("text/plain")); setDraggedId(null); }}>
                <time>{slot.time}</time><span><strong>{slot.site}</strong><small>{officer ? officer.name : "OPEN SHIFT"}</small></span>
                <i className={officer ? "is-filled" : ""}>{officer ? <Shield /> : <UserRound />}</i>
              </div>
            );
          })}
        </div>
      </div>
      <div className="mr-schedule-status"><span>{message}</span><Button type="button" variant="outline" size="icon" onClick={() => { setSlots(INITIAL_SLOTS); setMessage("SCHEDULE RESET"); }} aria-label="Reset scheduling demo"><RotateCcw /></Button></div>
    </div>
  );
}